// The document outline: the report's section list (from the agent-authored
// outline_json). Clicking a section scrolls the editor to it; the ✎ opens the
// command bar to rewrite just that section. The active section is highlighted as
// the cursor moves. Section order aligns with the editor's \section commands.
import { useTranslation } from "react-i18next";
import type { ReportOutline } from "@/api/report";

export function OutlinePanel({
  outline,
  activeIndex,
  onGoToSection,
  onEditSection,
}: {
  outline: ReportOutline | null;
  activeIndex: number;
  onGoToSection: (index: number) => void;
  onEditSection: (index: number, title: string) => void;
}) {
  const { t } = useTranslation();
  const sections = outline?.sections ?? [];

  if (sections.length === 0) {
    return (
      <div className="p-4 text-sm text-ink-faint">
        {t("The document outline will appear here once the report is drafted.")}
      </div>
    );
  }

  return (
    <div className="h-full space-y-2 overflow-auto p-3">
      {outline?.doc_title && (
        <p className="px-1 text-sm font-semibold text-ink">{outline.doc_title}</p>
      )}
      <ol className="space-y-0.5">
        {sections.map((s, i) => (
          <li key={i} className="group flex items-center gap-1">
            <button
              type="button"
              onClick={() => onGoToSection(i)}
              className={`flex-1 truncate rounded-md px-2 py-1 text-left text-xs ${
                i === activeIndex
                  ? "bg-brand-50 font-semibold text-brand-700"
                  : "text-ink-soft hover:bg-zinc-100"
              }`}
              title={s.title}
            >
              <span className="mr-1 text-ink-faint">{i + 1}.</span>
              {s.title || t("(untitled section)")}
            </button>
            <button
              type="button"
              onClick={() => onEditSection(i, s.title)}
              className="rounded-md px-1.5 py-1 text-xs text-ink-faint opacity-0 hover:bg-zinc-100 hover:text-brand-600 group-hover:opacity-100"
              title={t("Ask Eddi to rewrite this section")}
            >
              ✎
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
}
